import { z } from 'zod';
import {
  EducationLevel,
  EmploymentType,
  GenderPreference,
  JobStatus,
  SalaryPeriod,
  ShiftType,
  SkillCategory,
} from '../enums';
import { JOB_BENEFITS } from '../constants';
import { booleanish, dateSchema, optionalNumber, paginationSchema, zEnum } from './common';

export const jobSkillSchema = z.object({
  skillId: z.string().uuid(),
  isMandatory: z.boolean().default(false),
});

/** Plain object form — kept separate so `.partial()` still works for PATCH. */
export const jobBaseSchema = z.object({
  title: z.string().trim().min(3, 'Enter the job title').max(150),
  categoryId: z.string().uuid('Choose a job category'),
  locationId: z.string().uuid('Choose a factory / work location'),
  departmentId: z.string().uuid().optional(),
  description: z.string().trim().min(20, 'Describe the role in at least 20 characters').max(8000),
  responsibilities: z.string().trim().max(4000).optional().or(z.literal('')),

  employmentType: zEnum(EmploymentType).default(EmploymentType.PERMANENT),
  shift: zEnum(ShiftType).default(ShiftType.GENERAL),
  /** Head-count requested for this vacancy — the denominator of the fulfilment report. */
  vacancies: z.coerce.number().int().min(1, 'At least 1 vacancy').max(10_000),

  // --- Qualification -------------------------------------------------------
  // Floor and explicit set are independent filters; when both are supplied a
  // candidate must satisfy both.
  minQualification: zEnum(EducationLevel).default(EducationLevel.TENTH),
  acceptedQualifications: z.array(zEnum(EducationLevel)).max(9).default([]),
  /** e.g. ["Fitter", "Welder"] — matched against Education.courseOrTrade. */
  preferredTrades: z.array(z.string().trim().max(120)).max(20).default([]),

  minExperienceMonths: z.coerce.number().int().min(0).max(600).default(0),
  maxExperienceMonths: z.coerce.number().int().min(0).max(600).optional(),

  genderPreference: zEnum(GenderPreference).default(GenderPreference.ANY),
  minAge: z.coerce.number().int().min(14).max(65).optional(),
  maxAge: z.coerce.number().int().min(14).max(65).optional(),

  // --- Compensation --------------------------------------------------------
  // Above the line: what the worker is promised. `gt(0)` rather than `min(0)`
  // because a band starting at zero is "best in industry" wearing a number,
  // and the ad cannot draw its guaranteed line without a real floor.
  salaryMin: z.coerce.number().gt(0, 'Enter the guaranteed wage').max(10_000_000),
  salaryMax: z.coerce.number().gt(0, 'Enter the top of the band').max(10_000_000),
  salaryPeriod: zEnum(SalaryPeriod).default(SalaryPeriod.MONTHLY),
  isSalaryNegotiable: z.boolean().default(false),

  // Below the line: everything optional, because an employer who knows only
  // the band must still be able to publish. What they leave out, the ad shows
  // as a statutory floor rather than as their promise.
  //
  // Rupees and percent on the wire; the columns are paise and basis points and
  // the conversion happens at the API edge. See the Workers app's money-io.
  /** The share of gross that is basic — the base PF is charged on. */
  basicPercent: optionalNumber(z.coerce.number().min(1).max(100)),
  /** Per hour. Absent means the ad quotes the statutory double rate instead. */
  overtimeRate: optionalNumber(z.coerce.number().min(0).max(100_000)),
  attendanceIncentive: optionalNumber(z.coerce.number().min(0).max(1_000_000)),
  /** Per night worked. */
  nightShiftAllowance: optionalNumber(z.coerce.number().min(0).max(100_000)),
  joiningBonus: optionalNumber(z.coerce.number().min(0).max(1_000_000)),
  joiningBonusAfterDays: optionalNumber(z.coerce.number().int().min(0).max(365)),
  /** Taken off the guaranteed half, so it is a deduction and not a benefit. */
  accommodationDeduction: optionalNumber(z.coerce.number().min(0).max(1_000_000)),
  /** Hidden from seekers when false — employers often keep bands private. */
  showSalary: z.boolean().default(true),
  benefits: z.array(z.enum(JOB_BENEFITS)).default([]),

  skills: z.array(jobSkillSchema).max(30).default([]),

  interviewVenue: z.string().trim().max(300).optional().or(z.literal('')),
  interviewContact: z.string().trim().max(120).optional().or(z.literal('')),

  expiresAt: dateSchema.optional(),
  isUrgent: z.boolean().default(false),
  /** Bulk drive — unlocks batch interviews and bulk shortlisting in the UI. */
  isBulkHiring: z.boolean().default(false),
});

/** Cross-field rules applied on create. */
export const jobSchema = jobBaseSchema
  .refine((v) => v.salaryMax >= v.salaryMin, {
    message: 'Maximum salary cannot be less than minimum',
    path: ['salaryMax'],
  })
  // A waiting period with no bonus behind it is a promise of nothing, and it
  // renders on the ad as "after 90 days" attached to no money.
  .refine((v) => v.joiningBonusAfterDays === undefined || (v.joiningBonus ?? 0) > 0, {
    message: 'Add the bonus amount, or remove the waiting period',
    path: ['joiningBonusAfterDays'],
  })
  // The charge comes off the guaranteed half. Larger than the wage and the ad
  // prints a negative take-home, which is not a job.
  .refine((v) => (v.accommodationDeduction ?? 0) <= v.salaryMin, {
    message: 'Accommodation cannot cost more than the guaranteed wage',
    path: ['accommodationDeduction'],
  })
  .refine(
    (v) => v.maxExperienceMonths === undefined || v.maxExperienceMonths >= v.minExperienceMonths,
    { message: 'Maximum experience cannot be less than minimum', path: ['maxExperienceMonths'] },
  )
  .refine((v) => v.minAge === undefined || v.maxAge === undefined || v.maxAge >= v.minAge, {
    message: 'Maximum age cannot be less than minimum',
    path: ['maxAge'],
  });
export type JobInput = z.infer<typeof jobSchema>;

/** PATCH — every field optional; the service re-checks the cross-field rules on the merged row. */
export const updateJobSchema = jobBaseSchema.partial();
export type UpdateJobInput = z.infer<typeof updateJobSchema>;

export const jobStatusSchema = z.object({
  status: zEnum(JobStatus),
  remarks: z.string().trim().max(500).optional(),
});
export type JobStatusInput = z.infer<typeof jobStatusSchema>;

/** Public job search. Everything is optional so the bare endpoint lists all live jobs. */
export const jobSearchSchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  categoryId: z.string().uuid().optional(),
  categorySlug: z.string().trim().max(80).optional(),
  companyId: z.string().uuid().optional(),
  state: z.string().trim().max(100).optional(),
  district: z.string().trim().max(100).optional(),
  city: z.string().trim().max(100).optional(),
  shift: zEnum(ShiftType).optional(),
  employmentType: zEnum(EmploymentType).optional(),
  /** Seeker's own level — returns jobs whose qualification floor they clear. */
  qualification: zEnum(EducationLevel).optional(),
  minSalary: z.coerce.number().min(0).optional(),
  maxExperienceMonths: z.coerce.number().int().min(0).optional(),
  skillIds: z
    .union([z.array(z.string().uuid()), z.string().uuid().transform((v) => [v])])
    .optional(),
  benefits: z
    .union([z.array(z.enum(JOB_BENEFITS)), z.enum(JOB_BENEFITS).transform((v) => [v])])
    .optional(),
  isUrgent: booleanish.optional(),
  isBulkHiring: booleanish.optional(),
  postedWithinDays: z.coerce.number().int().min(1).max(365).optional(),
  /**
   * Distance search: jobs whose factory is within `radiusKm` of this PIN code.
   *
   * A PIN code rather than coordinates because that is what a candidate has -
   * asking a worker on a feature phone for latitude is not a real option, and
   * browser geolocation only helps the people already on a smartphone.
   */
  nearPincode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter a valid 6-digit PIN code')
    .optional(),
  /**
   * Capped at 200km: beyond that this stops being a commute and the candidate
   * is relocating, which is a different search with different questions
   * (accommodation, travel allowance) attached to it.
   */
  radiusKm: z.coerce.number().min(1).max(200).default(25),
});
export type JobSearchQuery = z.infer<typeof jobSearchSchema>;

export const jobCategorySchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase words separated by hyphens')
    .max(120)
    // Derived from the name when omitted; supplying it pins a public URL.
    .optional(),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  iconKey: z.string().trim().max(80).optional().or(z.literal('')),
  parentId: z.string().uuid().optional(),
  displayOrder: z.coerce.number().int().min(0).max(999).default(0),
  isActive: z.boolean().default(true),
});
export type JobCategoryInput = z.infer<typeof jobCategorySchema>;

export const skillSchema = z.object({
  name: z.string().trim().min(2).max(120),
  category: zEnum(SkillCategory).default(SkillCategory.OTHER),
  description: z.string().trim().max(300).optional().or(z.literal('')),
  isActive: z.boolean().default(true),
});
export type SkillInput = z.infer<typeof skillSchema>;

export const suggestQuerySchema = z.object({
  q: z.string().trim().min(1).max(80),
});
export type SuggestQuery = z.infer<typeof suggestQuerySchema>;

/**
 * One row of a job's pay ledger.
 *
 * `stated` is the load-bearing field. A figure the employer promised and a
 * figure we derived from statute are both numbers on a screen, and a worker
 * deciding whether to move district for the job has to be able to tell them
 * apart. Anything derived carries a `note` saying so.
 */
export interface PayLine {
  /** A stable key, so the interface can translate and order without parsing prose. */
  key: string;
  /** Rupees, as an exact decimal string — the same form every money field takes. */
  amount: string;
  /** Per month unless stated: an overtime rate is hourly, a night allowance per shift. */
  per?: 'month' | 'hour' | 'shift' | 'once';
  /** True when the employer stated it; false when we derived or defaulted it. */
  stated: boolean;
  /** Set when the figure is a floor or an estimate rather than a promise. */
  note?: string;
}

/**
 * What the money on a vacancy actually comes to.
 *
 * Guaranteed and conditional are separate arrays rather than one list with a
 * flag, because the one thing this shape must make impossible is adding them
 * together. That sum is how a job advertises 25,000 and pays 18,500.
 */
export interface PayBreakdown {
  /** What the worker is promised for a full month, before deductions. */
  guaranteed: PayLine[];
  /** Taken off that. Estimates, and labelled as such. */
  deductions: PayLine[];
  /** Guaranteed minus deductions. The number people actually want. */
  takeHome: { min: string; max: string; estimated: true };
  /** Money that depends on doing something — attendance, nights, staying. */
  conditional: PayLine[];
  /** True when the employer has filled in anything beyond the range. */
  detailed: boolean;
  /** When the employer last confirmed these figures. Null is shown as unconfirmed. */
  confirmedAt: string | null;
}

/**
 * The two facts a job card needs from a ledger.
 *
 * A search page carries twenty cards. The card shows what lands in a hand and
 * whether anything else is on offer; sending the whole breakdown twenty times
 * to render that is fifteen kilobytes of detail nobody reads, on the handsets
 * this platform is built around.
 *
 * Conditional money is named, never priced. A figure on a card, separated from
 * the condition attached to it, is the confusion the ledger exists to remove —
 * the amounts belong on the ad, next to what you have to do to earn them.
 */
export interface PaySummary {
  takeHome: { min: string; max: string };
  /** The `key` of each conditional line, in the order the ledger lists them. */
  conditional: string[];
}

export function summarisePay(pay: PayBreakdown): PaySummary {
  return {
    takeHome: { min: pay.takeHome.min, max: pay.takeHome.max },
    conditional: pay.conditional.map((line) => line.key),
  };
}
